import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import {
  BarChart3,
  Bot,
  GraduationCap,
  Mail,
  Users,
  User,
  LogOut,
  BookOpen,
  Sparkles,
  Info,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { clearAuthTokens, getPortalType, getUserData, clearAllSessions, getAuthToken } from '@/utils/auth';
import { apiUrl } from '@/api/config';
import axios from 'axios';

const menuItems = [
  { id: 'dashboard', label: 'DASHBOARD', icon: BarChart3, color: 'text-[#C9A961]', type: 'regular' },
  { id: 'my-classes', label: 'STUDENT INSIGHTS', icon: Users, color: 'text-[#D4AF77]', type: 'regular' },
  { id: 'curiosity-analytics', label: 'CURIOSITY ANALYTICS', icon: Sparkles, color: 'text-[#C9A961]', type: 'regular' },
  { id: 'notes', label: 'CLASS NOTES', icon: BookOpen, color: 'text-[#9AD5D0]', type: 'regular' },
  { id: 'ai-tools', label: 'AI TOOLS', icon: Bot, color: 'text-[#C9A961]', type: 'regular' },
  { id: 'training', label: 'TEACHER TRAINING', icon: GraduationCap, color: 'text-[#C9A961]', type: 'regular' },
  { id: 'parent-updates', label: 'PARENT UPDATES', icon: Mail, color: 'text-[#D4AF77]', type: 'regular' },
  { id: 'about', label: 'ABOUT', icon: Info, color: 'text-[#9AD5D0]', type: 'regular' },
];

export default function TeacherSidebar({ activeSection, setActiveSection, className, onNavigate, showCloseButton = false, onClose = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { teacherTheme } = useTeacher();
  const isDark = teacherTheme === 'dark';

  const handleItemClick = (itemId) => {
    // Set active section for all items
    setActiveSection(itemId);

    if (itemId === 'dashboard') {
      navigate('/teacher/dashboard');
    } else if (itemId === 'my-classes') {
      navigate('/teacher/my-classes');
    } else if (itemId === 'ai-tools') {
      navigate('/teacher/ai-tools');
    } else if (itemId === 'notes') {
      navigate('/teacher/notes');
    } else if (itemId === 'training') {
      navigate('/teacher/training');
    } else if (itemId === 'curiosity-analytics') {
      navigate('/teacher/curiosity-analytics');
    } else if (itemId === 'parent-updates') {
      navigate('/teacher/parent-updates');
    } else if (itemId === 'community') {
      navigate('/teacher/community');
    } else if (itemId === 'about') {
      navigate('/teacher/about');
    }

    if (onNavigate) onNavigate();
  };

  return (
    <div className={cn("min-w-fit w-full max-w-[3.5rem] sm:max-w-xs sm:max-w-sm h-full border-r flex flex-col transition-all duration-300 bg-[hsl(var(--sidebar-bg))] sm:bg-[hsl(var(--sidebar-bg))]", className,
      isDark
        ? "border-[hsl(var(--border))]"
        : "border-[#E8E1D6]"
    )}>
      {/* Close Button for Mobile */}
      {showCloseButton && (
        <div className="p-4 flex justify-end lg:hidden">
          <button
            onClick={onClose}
            className="p-1 rounded-full bg-white/90 hover:bg-white shadow z-50"
            title="Close sidebar"
          >
            <X className="w-4 h-4 text-gray-700" />
          </button>
        </div>
      )}
      {/* Section 1: Logo + Brand Name - Gold/Mustard Theme */}
      <div className={cn("p-4 sm:p-6 pb-4 sm:pb-6 border-b-2",
        isDark
          ? "bg-gradient-to-br from-[hsl(var(--gold-primary))] to-[hsl(var(--gold-secondary))] border-[hsl(var(--gold-primary))]/50"
          : "bg-gradient-to-br from-[#E8DCC8] to-[#D4C4A8] border-[#C9B896]"
      )}>
        <div className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Budlee AI Logo"
            className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0"
          />
          <div className="flex-1 flex flex-col items-start gap-1">
            <img 
              src="/logo_text.png" 
              alt="Budlee AI" 
              className="h-5 sm:h-6 object-contain w-auto" 
            />
            <p className={cn(
              "text-[10px] uppercase tracking-wider font-bold",
              isDark ? "text-[#1A1A1A]/70" : "text-[#2B2218]/60"
            )}>Teacher Portal</p>
          </div>
        </div>
      </div>

      {/* Section 2: Menu Items - Main Navigation */}
      <nav className={cn("flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1 transition-colors",
        isDark ? "bg-[hsl(var(--main-bg))]" : "bg-[#FAF8F5]"
      )}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item.id)}
              className={cn(
                'w-full flex items-center gap-4 px-4 py-3.5 rounded-xl font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
                isActive
                  ? isDark
                    ? 'bg-[hsl(var(--sidebar-hover))] text-[hsl(var(--foreground))] shadow-md'
                    : 'bg-[#F5F1EB] text-[#2B2B2B] shadow-md'
                  : isDark
                    ? 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--sidebar-hover))]/50 hover:text-[hsl(var(--foreground))]'
                    : 'text-[#2B2B2B] hover:bg-[#F5F1EB]/50 hover:text-black'
              )}
            >
              {isActive && (
                <div className={cn("absolute inset-0 rounded-xl p-[2px] bg-gradient-to-r from-[#C9A961] via-[#D4AF77] to-[#C9A961] animate-pulse",
                  isDark && "from-[hsl(var(--gold-primary))] via-[hsl(var(--gold-secondary))] to-[hsl(var(--gold-primary))]"
                )}>
                  <div className={cn("w-full h-full bg-[#F5F1EB] rounded-xl",
                    isDark && "bg-[hsl(var(--sidebar-hover))]"
                  )} />
                </div>
              )}

              <div className="relative z-10 flex items-center gap-4 w-full">
                <Icon className={cn('w-5 h-5 flex-shrink-0', item.color)} />
                <span className="flex-1 text-left">{item.label}</span>
              </div>
            </button>
          );
        })}
      </nav>

      {/* Section 3: Community - Bottom with Logo Background */}
      <div className={cn("border-t-2 transition-colors",
        isDark
          ? "bg-gradient-to-br from-[hsl(var(--gold-primary))] to-[hsl(var(--gold-secondary))] border-[hsl(var(--gold-primary))]/50"
          : "bg-gradient-to-br from-[#E8DCC8] to-[#D4C4A8] border-[#C9B896]"
      )}>
        <button
          onClick={() => handleItemClick('community')}
          className={cn(
            'w-full flex items-center gap-4 px-4 py-3.5 font-bold text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
            isDark ? 'text-[#1A1A1A]' : 'text-[#2B2B2B]'
          )}
        >
          {activeSection === 'community' && (
            <div className={cn("absolute inset-0 p-[2px] bg-gradient-to-r from-[#C9A961] via-[#D4AF77] to-[#C9A961] animate-pulse",
              isDark && "from-[hsl(var(--gold-primary))] via-[hsl(var(--gold-secondary))] to-[hsl(var(--gold-primary))]"
            )}>
              <div className={cn("w-full h-full bg-gradient-to-br from-[#E8DCC8] to-[#D4C4A8]",
                isDark && "bg-gradient-to-br from-[hsl(var(--gold-primary))] to-[hsl(var(--gold-secondary))]"
              )} />
            </div>
          )}
          <div className="relative z-10 flex items-center gap-4 w-full">
            <Users className={cn("w-5 h-5 flex-shrink-0", isDark ? "text-[#1A1A1A]/70" : "text-[#3D2B1F]/70")} />
            <span className="flex-1 text-left">COMMUNITY</span>
          </div>
        </button>
      </div>
    </div>
  );
}

